/**
 * Объявления компаниям (ЛЦ-13): кому (администраторам или всем сотрудникам), каким компаниям, на какой срок,
 * важность и рассылка письмом и в Telegram; сколько людей получат — до отправки; отмена.
 */
import {
  ANNOUNCEMENT_AUDIENCE_TITLES,
  ANNOUNCEMENT_AUDIENCES,
  ANNOUNCEMENT_LEVEL_TITLES,
  ANNOUNCEMENT_LEVELS,
  ANNOUNCEMENT_STATE_TITLES,
  type Announcement,
  type AnnouncementAudience,
  type AnnouncementLevel,
  type AnnouncementPreview,
  type ConsoleCompany,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { dateTime, plural } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const STATE_TONE = { scheduled: 'info', active: 'success', ended: 'neutral', cancelled: 'neutral' } as const;

/** Локальное «ГГГГ-ММ-ДДTЧЧ:ММ» для поля даты и времени */
function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function NewAnnouncementDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const companies = useQuery({
    queryKey: ['platform', 'companies'],
    queryFn: () => api<ConsoleCompany[]>('/platform/companies'),
  });
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [level, setLevel] = useState<AnnouncementLevel>('info');
  const [audience, setAudience] = useState<AnnouncementAudience>('admins');
  const [all, setAll] = useState(true);
  const [picked, setPicked] = useState<string[]>([]);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState(() => localInput(new Date(Date.now() + 3 * 86_400_000)));
  const [notify, setNotify] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const tenantIds = all ? null : picked;
  const previewKey = useMemo(() => JSON.stringify({ audience, tenantIds }), [audience, tenantIds]);
  const preview = useQuery({
    queryKey: ['platform', 'announcements', 'preview', previewKey],
    queryFn: () =>
      api<AnnouncementPreview>('/platform/announcements/preview', { body: { audience, tenantIds } }),
    enabled: all || picked.length > 0,
  });

  const save = useMutation({
    mutationFn: () =>
      api<Announcement>('/platform/announcements', {
        body: {
          title,
          body,
          level,
          audience,
          tenantIds,
          startsAt: startsAt ? new Date(startsAt).toISOString() : null,
          endsAt: endsAt ? new Date(endsAt).toISOString() : '',
          notify,
        },
      }),
    onSuccess: (a) => {
      void qc.invalidateQueries({ queryKey: ['platform', 'announcements'] });
      toast(a.state === 'scheduled' ? 'Объявление запланировано' : 'Объявление показывается');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    if (!all && !picked.length) {
      setErrors({ tenantIds: 'Выберите хотя бы одну компанию' });
      return;
    }
    save.mutate();
  }

  const list = (companies.data ?? []).filter((c) => c.status === 'active');
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      width="lg"
      title="Новое объявление"
      description="Плашка вверху портала у получателей до конца показа или пока человек не нажмёт «Скрыть»."
      footer={
        <>
          <span className="mr-auto text-[13px] text-ink-2">
            {preview.data
              ? `Получат: ${plural(preview.data.recipients, 'человек', 'человека', 'человек')} в ${plural(preview.data.companies, 'компании', 'компаниях', 'компаниях')}`
              : ''}
          </span>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="announcement-form" loading={save.isPending}>
            {startsAt ? 'Запланировать' : 'Показать'}
          </Button>
        </>
      }
    >
      <form id="announcement-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label="Заголовок" htmlFor="a-title" error={errors.title} className="sm:col-span-2">
          <Input
            id="a-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="Например: плановые работы в субботу"
            invalid={!!errors.title}
          />
        </Field>
        <Field label="Текст" htmlFor="a-body" error={errors.body} className="sm:col-span-2">
          <Textarea
            id="a-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            placeholder="Что случится, когда и что сделать людям"
            invalid={!!errors.body}
          />
        </Field>
        <Field label="Кому" htmlFor="a-audience">
          <Select
            id="a-audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value as AnnouncementAudience)}
          >
            {ANNOUNCEMENT_AUDIENCES.map((a) => (
              <option key={a} value={a}>
                {ANNOUNCEMENT_AUDIENCE_TITLES[a]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Важность" htmlFor="a-level" hint="Важное — жёлтая плашка выше остальных">
          <Select id="a-level" value={level} onChange={(e) => setLevel(e.target.value as AnnouncementLevel)}>
            {ANNOUNCEMENT_LEVELS.map((l) => (
              <option key={l} value={l}>
                {ANNOUNCEMENT_LEVEL_TITLES[l]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Начало показа" htmlFor="a-start" hint="Пусто — сразу" error={errors.startsAt}>
          <Input
            id="a-start"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            invalid={!!errors.startsAt}
          />
        </Field>
        <Field label="Конец показа" htmlFor="a-end" error={errors.endsAt}>
          <Input
            id="a-end"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            invalid={!!errors.endsAt}
          />
        </Field>
        <div className="flex flex-col gap-2 sm:col-span-2">
          <Checkbox
            label="Всем компаниям"
            description="Кроме демо-компании и приостановленных. Снимите, чтобы выбрать компании"
            checked={all}
            onChange={(e) => setAll(e.target.checked)}
          />
          {all ? null : companies.isPending ? (
            <Spinner />
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-md border border-line-strong p-2">
              {list.map((c) => (
                <Checkbox
                  key={c.id}
                  className="min-h-11 items-center px-1 md:min-h-8"
                  label={`${c.name}${c.isDemo ? ' (демо)' : ''}`}
                  checked={picked.includes(c.id)}
                  onChange={(e) =>
                    setPicked((p) => (e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id)))
                  }
                />
              ))}
            </div>
          )}
          {errors.tenantIds ? (
            <p className="text-[12px] text-danger" role="alert">
              {errors.tenantIds}
            </p>
          ) : null}
        </div>
        <Checkbox
          className="sm:col-span-2"
          label="Отправить письмом и в Telegram"
          description="По настройкам уведомлений каждого человека, один раз в начале показа"
          checked={notify}
          onChange={(e) => setNotify(e.target.checked)}
        />
        <ErrorBox className="sm:col-span-2" error={Object.keys(errors).length ? null : save.error} />
      </form>
    </Dialog>
  );
}

export function AnnouncementsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    queryKey: ['platform', 'announcements'],
    queryFn: () => api<Announcement[]>('/platform/announcements'),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/platform/announcements/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform', 'announcements'] });
      toast('Объявление отменено');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось', 'danger'),
  });
  return (
    <>
      <Panel
        title="Объявления компаниям"
        meta={q.data ? `${q.data.length}` : undefined}
        actions={
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="size-4" />}
            onClick={() => setCreating(true)}
          >
            Новое объявление
          </Button>
        }
        bodyClassName="p-0"
      >
        {q.isPending ? (
          <div className="px-5">
            <Spinner />
          </div>
        ) : q.isError ? (
          <div className="p-5">
            <ErrorBox error={q.error} />
          </div>
        ) : !q.data.length ? (
          <div className="p-5">
            <EmptyState title="Объявлений пока нет">
              Сообщите администраторам или всем сотрудникам компаний о работах на портале, новых разделах или
              сроках — плашкой в портале и, по желанию, письмом.
            </EmptyState>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {q.data.map((a) => (
              <li key={a.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-start sm:gap-4">
                <div className="min-w-0 flex-1 text-[14px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink [overflow-wrap:anywhere]">{a.title}</span>
                    <Badge tone={STATE_TONE[a.state]}>{ANNOUNCEMENT_STATE_TITLES[a.state]}</Badge>
                    {a.level === 'important' ? <Badge tone="warning">важное</Badge> : null}
                  </div>
                  <p className="mt-0.5 whitespace-pre-line text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                    {a.body}
                  </p>
                  <p className="mt-1 text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                    {ANNOUNCEMENT_AUDIENCE_TITLES[a.audience]} ·{' '}
                    {a.tenantIds ? a.tenantNames.join(', ') : 'всем компаниям'} · {dateTime(a.startsAt)} —{' '}
                    {dateTime(a.endsAt)}
                  </p>
                  <p className="text-[13px] text-ink-2">
                    Получателей: {a.recipients}, скрыли: {a.dismissed}
                    {a.notify
                      ? a.notifiedAt
                        ? ` · писем и сообщений в очереди: ${a.queued}`
                        : ' · письма уйдут в начале показа'
                      : ''}
                    {a.createdByName ? ` · ${a.createdByName}` : ''}
                  </p>
                </div>
                {a.state === 'active' || a.state === 'scheduled' ? (
                  <Button
                    size="sm"
                    variant="danger"
                    loading={cancel.isPending && cancel.variables === a.id}
                    onClick={() => cancel.mutate(a.id)}
                  >
                    Отменить
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {creating ? <NewAnnouncementDialog onClose={() => setCreating(false)} /> : null}
    </>
  );
}
